// « Une nouvelle version du bureau est en ligne. » [Recharger]: a page that stayed open through a
// deploy keeps running the bundle it loaded, while the live stream and the bootstrap keep working
// against the new server. The server tells the build it serves (SSE `hello`, bootstrap
// `office.build`, the X-Office-Build header → store `serverBuild`); when it is not this page's
// (web/version.js), a persistent banner offers to reload. The page reloads by itself only when
// nobody is looking at it (hidden tab) and nothing would be lost, at most once per build.
import { h, button } from '../dom.js';
import { t } from '../i18n/fr.js';
import { CLIENT_BUILD, BUILD_ID_RE } from '../version.js';

/** sessionStorage key: the build this tab last reloaded for (one automatic reload per build). */
export const RELOADED_KEY = 'mo.reloadedFor';
/** While hidden and stale but busy, the automatic reload is tried again this often. */
export const RETRY_MS = 30000;

/** Input types whose value is not text being typed. */
const NOT_TYPED = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);

/**
 * True when the server serves another build than this page's (pure). False when either is
 * unknown: a page without an id (tests, `--watch`) or a server mid-deploy (null) is never stale.
 * @param {string|null} client
 * @param {unknown} server
 */
export function isStale(client, server) {
  return Boolean(client && typeof server === 'string' && BUILD_ID_RE.test(server) && server !== client);
}

/** True when `el` holds something the person is typing (pure but for the element's fields). */
function typing(el) {
  if (!el || el.isConnected === false) return false;
  if (el.isContentEditable) return true;
  const tag = String(el.tagName || '').toUpperCase();
  if (tag === 'INPUT' && NOT_TYPED.has(String(el.type || el.getAttribute?.('type') || 'text').toLowerCase())) return false;
  if (tag !== 'INPUT' && tag !== 'TEXTAREA') return false;
  return String(el.value ?? '').trim() !== '';
}

/**
 * True when reloading the page now would lose nothing: no write on its way (a message, an upload,
 * a connector being added), no layer open (a drawer, a dialog, a menu may hold a form), no studio
 * upload or media playing (`isBusy`), no text being typed in the focused field.
 * @param {{overlay?: {count(): number}, doc?: Document, pendingWrites?: () => number, isBusy?: () => boolean}} [opts]
 * @returns {boolean}
 */
export function reloadIsSafe({ overlay, doc = globalThis.document, pendingWrites = () => 0, isBusy = () => false } = {}) {
  if (pendingWrites() > 0) return false;
  if ((overlay?.count?.() ?? 0) > 0) return false;
  if (isBusy()) return false;
  return !typing(doc?.activeElement);
}

/** sessionStorage, or nothing when the browser refuses it (private mode, blocked storage). */
export function sessionStore() {
  return {
    get(key) {
      try { return globalThis.sessionStorage?.getItem(key) ?? null; } catch { return null; }
    },
    set(key, value) {
      try { globalThis.sessionStorage?.setItem(key, value); } catch { /* refused: get() tells */ }
    },
  };
}

function defaultReload() {
  globalThis.location?.reload();
}

/**
 * Reloads for `target` unless this tab already did: the build is written down first and read back,
 * so a tab whose storage does not keep it never reloads by itself (it could loop).
 * @returns {boolean} true when the reload was asked
 */
function reloadOnce(target, { storage, reload }) {
  if (storage.get(RELOADED_KEY) === target) return false;
  storage.set(RELOADED_KEY, target);
  if (storage.get(RELOADED_KEY) !== target) return false;
  reload();
  return true;
}

/**
 * At start, before anything is mounted (web/main.js startApp): a page whose bundle is older than
 * the build served reloads at once, once per build (a stale copy served again stays put and gets
 * the banner instead).
 * @param {unknown} serverBuild bootstrap `office.build`
 * @param {{clientBuild?: string|null, storage?: {get(k: string): string|null, set(k: string, v: string): void},
 *   reload?: () => void}} [opts]
 * @returns {boolean} true when the page is reloading
 */
export function reloadIfStale(serverBuild, { clientBuild = CLIENT_BUILD, storage = sessionStore(), reload = defaultReload } = {}) {
  if (!isStale(clientBuild, serverBuild)) return false;
  return reloadOnce(serverBuild, { storage, reload });
}

/**
 * Creates the banner and follows `store.serverBuild`. « Recharger » always reloads; the page
 * reloads by itself only while hidden and `reloadIsSafe`, at most once per build.
 * @param {{store: object, overlay?: {count(): number}}} ctx
 * @param {{host: HTMLElement, clientBuild?: string|null, pendingWrites?: () => number,
 *   isBusy?: () => boolean, reload?: () => void, storage?: {get(k: string): string|null, set(k: string, v: string): void},
 *   isHidden?: () => boolean, doc?: Document}} opts
 * @returns {{el: HTMLElement, isShown(): boolean, markStale(): void, destroy(): void}}
 */
export function createUpdateNotice(ctx, {
  host, clientBuild = CLIENT_BUILD, pendingWrites = () => 0, isBusy = () => false, reload = defaultReload,
  storage = sessionStore(), doc = globalThis.document, isHidden = () => doc?.visibilityState === 'hidden',
}) {
  const { store, overlay } = ctx;
  const el = h('div', { class: 'update-banner', role: 'status', 'aria-live': 'polite', hidden: true },
    h('span', { class: 'update-banner-text' }, t('app.updateAvailable')),
    button(t('app.updateReload'), { variant: 'primary', size: 'sm', class: 'update-banner-btn', onClick: () => reloadNow() }));
  host.appendChild(el);
  let forced = false; // a write was refused as stale (409) even if the build is not known here
  let retry = null;

  const serverBuild = () => store.get().serverBuild;
  const stale = () => forced || isStale(clientBuild, serverBuild());
  const target = () => (isStale(clientBuild, serverBuild()) ? serverBuild() : 'staleClient');

  function reloadNow() {
    storage.set(RELOADED_KEY, target());
    reload();
  }

  function stopRetry() {
    clearTimeout(retry);
    retry = null;
  }

  function maybeAuto() {
    stopRetry();
    if (!stale() || !isHidden()) return;
    if (storage.get(RELOADED_KEY) === target()) return;
    if (!reloadIsSafe({ overlay, doc, pendingWrites, isBusy })) {
      retry = setTimeout(maybeAuto, RETRY_MS);
      return;
    }
    reloadOnce(target(), { storage, reload });
  }

  function check() {
    // The server is back on this page's build (a rollback): nothing to reload any more.
    if (serverBuild() && serverBuild() === clientBuild) forced = false;
    el.hidden = !stale();
    maybeAuto();
  }

  const onVisibility = () => maybeAuto();
  const off = store.on('serverBuild', check);
  doc?.addEventListener?.('visibilitychange', onVisibility);
  check();
  return {
    el,
    isShown: () => !el.hidden,
    /** A write was refused because this page is outdated (409 staleClient): show the banner. */
    markStale() {
      forced = true;
      check();
    },
    destroy() {
      off();
      stopRetry();
      doc?.removeEventListener?.('visibilitychange', onVisibility);
      el.remove();
    },
  };
}
